import { collection, getDocs, limit, orderBy, query, Timestamp, where } from 'firebase/firestore'
import { db } from '../firebase/firebase'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createFetchThunk } from '../store/fetch'
import { dateFormatUS, setEndDate, setStartDate } from '../dateFormat'
import { IS_DEV } from '../../constants'

const access = ['superadmin', 'admin', 'operator', 'developer']

const q = query(collection(db, 'cards'), orderBy('createdDate', 'desc'))
const DEFAULT_CLOSED_CARDS_LIMIT = 200

const allClosedCardsQuery = query(
  collection(db, 'cards'),
  where('status', '==', 2),
  orderBy('extremeDateReal', 'desc')
)
// Consulta para todas las tarjetas abiertas
const openCardsQuery = query(
  collection(db, 'cards'),
  where('status', '<', 2),
  orderBy('status', 'asc'),
  orderBy('createdDate', 'desc')
)

const today = new Date()
const beginMonth = new Date(today.getFullYear(), today.getMonth(), 1)
// const twoMonthAgo = new Date(new Date().setMonth(today.getMonth() - 2))
export const fetchCards = createFetchThunk('cards', q, access)
export const fetchAllClosedCards = createFetchThunk('closedCards', allClosedCardsQuery, access)
export const fetchOpenCards = createFetchThunk('openCards', openCardsQuery, access)

export const fetchClosedCards = createAsyncThunk(
  'closedCards/fetch',
  async (arg, thunkAPI) => {
    const userData = thunkAPI.getState().user.userData
    let closedCardsQuery

    if (
      arg &&
      typeof arg === 'object' &&
      typeof arg.closedWithinDays === 'number' &&
      arg.closedWithinDays > 0
    ) {
      const since = new Date()
      since.setDate(since.getDate() - arg.closedWithinDays)
      since.setHours(0, 0, 0, 0)
      const fromTs = Timestamp.fromDate(since)
      closedCardsQuery = query(
        collection(db, 'cards'),
        where('status', '==', 2),
        where('extremeDateReal', '>=', fromTs),
        orderBy('extremeDateReal', 'desc')
      )
    } else {
      const maxClosedCards =
        typeof arg === 'number' && arg > 0 ? arg : DEFAULT_CLOSED_CARDS_LIMIT
      closedCardsQuery = query(
        collection(db, 'cards'),
        where('status', '==', 2),
        orderBy('extremeDateReal', 'desc'),
        limit(maxClosedCards)
      )
    }

    const querySnapshot = await getDocs(closedCardsQuery)
    const items = []
    querySnapshot.forEach((docSnap) => {
      if (access.includes(userData.access)) {
        items.push({ id: docSnap.id, ...docSnap.data() })
      }
    })
    IS_DEV && console.error('Fetch closedCards', arg, items.length)
    return items
  }
)

export const fetchCardsForDateRange = createAsyncThunk(
  'cardsInDateRange/fetch',
  async ({ fromDate, toDate }, thunkAPI) => {
    const userData = thunkAPI.getState().user.userData
    const start = setStartDate(fromDate)
    const end = setEndDate(toDate)
    if (!start || !end) {
      throw new Error('Invalid date range')
    }
    const maxAllowedEnd = new Date(start)
    maxAllowedEnd.setMonth(maxAllowedEnd.getMonth() + 12)
    if (end > maxAllowedEnd) {
      throw new Error('Date range cannot exceed 12 months')
    }
    const fromTs = Timestamp.fromDate(start)
    const toTs = Timestamp.fromDate(end)
    const rangeQuery = query(
      collection(db, 'cards'),
      where('createdDate', '>=', fromTs),
      where('createdDate', '<=', toTs),
      orderBy('createdDate', 'desc')
    )
    const querySnapshot = await getDocs(rangeQuery)
    const items = []
    querySnapshot.forEach((docSnap) => {
      if (access.includes(userData.access)) {
        items.push({ id: docSnap.id, ...docSnap.data() })
      }
    })
    IS_DEV && console.error('Fetch cardsInDateRange', fromDate, toDate, items.length)
    return items
  }
)

const initialState = {
  // fromDate: dateFormatUS(twoMonthAgo),
  fromDate: dateFormatUS(beginMonth),
  toDate: dateFormatUS(today),
  locationId: '',
  searchTerm: '',
  type: '',
  sort: {
    key: 'createdDate',
    direction: 'caret-up',
  },
  status: '',
  createdBy: '',
  otStatus: '',
}
const cardsSlice = createSlice({
  name: 'cards',
  initialState: {
    data: {
      open: [],
      closed: [],
    },
    status: {
      open: 'idle',
      closed: 'idle',
    },
    error: null,
    lastFetchedDateRangeKey: null,
    failedDateRangeKey: null,
    filters: {
      ...initialState,
    },
  },
  reducers: {
    cardAdded: (state, action) => {
      state.data.open.push(action.payload)
    },
    openCardsUpdated: (state, action) => {
      state.data.open = action.payload
    },
    closedCardsUpdated: (state, action) => {
      state.data.closed = action.payload
    },
    cardUpdated: (state, action) => {
      const index = state.data.open.findIndex((card) => card.id === action.payload.id)
      if (index !== -1) {
        if (action.payload.status === 2) {
          state.data.closed.push(action.payload)
          state.data.open.splice(index, 1)
        } else {
          state.data.open[index] = action.payload
        }
      }
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },
    clearFilters: (state) => {
      const t = new Date()
      const bm = new Date(t.getFullYear(), t.getMonth(), 1)
      state.filters = {
        ...initialState,
        fromDate: dateFormatUS(bm),
        toDate: dateFormatUS(t),
      }
    },
    removeFilter: (state, action) => {
      const { payload } = action
      if (payload === 'fromDate' || payload === 'toDate') {
        // preserve date filters
        // delete state.filters.fromDate
        // delete state.filters.toDate
      } else {
        delete state.filters[payload]
      }
    },
    reload: (state) => {
      state.status.open = 'idle'
      state.status.closed = 'idle'
      state.lastFetchedDateRangeKey = null
      state.failedDateRangeKey = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOpenCards.pending, (state) => {
        state.status.open = 'loading'
      })
      .addCase(fetchOpenCards.fulfilled, (state, action) => {
        state.status.open = 'succeeded'
        state.data.open = action.payload
      })
      .addCase(fetchOpenCards.rejected, (state, action) => {
        state.status.open = 'failed'
        state.error = action.error.message
      })
      .addCase(fetchClosedCards.pending, (state) => {
        state.status.closed = 'loading'
      })
      .addCase(fetchClosedCards.fulfilled, (state, action) => {
        state.status.closed = 'succeeded'
        state.data.closed = action.payload
      })
      .addCase(fetchClosedCards.rejected, (state, action) => {
        state.status.closed = 'failed'
        state.error = action.error.message
      })
      .addCase(fetchCardsForDateRange.pending, (state) => {
        state.status.open = 'loading'
        state.status.closed = 'loading'
        state.failedDateRangeKey = null
      })
      .addCase(fetchCardsForDateRange.fulfilled, (state, action) => {
        state.status.open = 'succeeded'
        state.status.closed = 'succeeded'
        state.failedDateRangeKey = null
        const arg = action.meta.arg
        state.lastFetchedDateRangeKey =
          arg?.fromDate && arg?.toDate ? `${arg.fromDate}|${arg.toDate}` : null
        const items = action.payload
        state.data.open = items.filter((c) => typeof c.status === 'number' && c.status < 2)
        state.data.closed = items.filter((c) => c.status === 2)
      })
      .addCase(fetchCardsForDateRange.rejected, (state, action) => {
        state.status.open = 'failed'
        state.status.closed = 'failed'
        state.error = action.error.message
        const arg = action.meta.arg
        state.failedDateRangeKey =
          arg?.fromDate && arg?.toDate ? `${arg.fromDate}|${arg.toDate}` : null
      })
  },
})

export const {
  cardAdded,
  cardUpdated,
  openCardsUpdated,
  closedCardsUpdated,
  setFilters,
  clearFilters,
  removeFilter,
  reload,
} = cardsSlice.actions
export default cardsSlice.reducer
